import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CatalogTable,
  ChangeLink,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  SettingsCard,
  StatusBanner,
  Toggle,
} from "containers/settings/ui";
import { FuelBarcode, FuelBarcodeType } from "./fuelBarcodes.types";
import { fetchFuelBarcodes, saveFuelBarcode } from "./fuelBarcodes.service";
import { FuelBarcodeEditor } from "./FuelBarcodeEditor";
import {
  DEFAULT_FUEL_BARCODE_ENTRIES,
  fuelBarcodeKey,
  sortFuelBarcodes,
} from "./fuelBarcodeDefaults";

// The fuel-card provider whose statements are recognised by each kind of match.
const PROVIDER_BY_TYPE: Record<FuelBarcodeType, string> = {
  barcode: "Rotada",
  productClass: "E100",
  serviceType: "Korta",
};

const TYPE_LABEL_KEYS: Record<FuelBarcodeType, string> = {
  barcode: "fuel_barcode_type_barcode",
  productClass: "fuel_barcode_type_product_class",
  serviceType: "fuel_barcode_type_service_type",
};

export interface FuelBarcodesPanelProps {
  companyId: string;
}

export function FuelBarcodesPanel({ companyId }: FuelBarcodesPanelProps) {
  const { t } = useTranslation();
  const [barcodes, setBarcodes] = useState<FuelBarcode[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  // The entry open in the editor; "new" while adding one; null when closed.
  const [editing, setEditing] = useState<FuelBarcode | "new" | null>(null);
  const [pendingToggleIds, setPendingToggleIds] = useState<Set<string>>(
    () => new Set()
  );
  const pendingToggleIdsRef = useRef<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    try {
      const rows = await fetchFuelBarcodes(companyId);
      setBarcodes(sortFuelBarcodes(rows || []));
    } catch (e) {
      console.error("[fuel-barcodes] Failed to load", e);
      toast.error(t("fuel_barcode_error_loading"));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleActive = async (barcode: FuelBarcode) => {
    if (pendingToggleIdsRef.current.has(barcode.id)) return;
    pendingToggleIdsRef.current.add(barcode.id);
    setPendingToggleIds((prev) => new Set(prev).add(barcode.id));
    const nextActive = !barcode.isActive;
    // Optimistic update so the toggle flips instantly with no table reload/flash.
    setBarcodes((prev) =>
      prev.map((b) =>
        b.id === barcode.id ? { ...b, isActive: nextActive } : b
      )
    );
    try {
      await saveFuelBarcode(companyId, {
        id: barcode.id,
        type: barcode.type,
        value: barcode.value,
        description: barcode.description,
        isActive: nextActive,
      });
    } catch (e) {
      // Revert on failure.
      setBarcodes((prev) =>
        prev.map((b) =>
          b.id === barcode.id ? { ...b, isActive: barcode.isActive } : b
        )
      );
      console.error("[fuel-barcodes] Failed to toggle", e);
      toast.error(t("fuel_barcode_error_saving"));
    } finally {
      pendingToggleIdsRef.current.delete(barcode.id);
      setPendingToggleIds((prev) => {
        const next = new Set(prev);
        next.delete(barcode.id);
        return next;
      });
    }
  };

  const handleImportDefaults = async () => {
    // Skip any default that already exists for this company (type + normalized value).
    const existingKeys = new Set(barcodes.map(fuelBarcodeKey));
    const toImport = DEFAULT_FUEL_BARCODE_ENTRIES.filter(
      (entry) => !existingKeys.has(fuelBarcodeKey(entry))
    );

    if (toImport.length === 0) {
      toast.info(t("fuel_barcode_defaults_already_present"));
      return;
    }

    try {
      setImporting(true);
      const results = await Promise.allSettled(
        toImport.map((entry) =>
          saveFuelBarcode(companyId, {
            type: entry.type,
            value: entry.value,
            isActive: true,
          })
        )
      );
      await loadData();

      const failures = results.filter((result) => result.status === "rejected");
      if (failures.length > 0) {
        console.error("[fuel-barcodes] Failed to import defaults", failures);
        toast.error(t("fuel_barcode_error_saving"));
        return;
      }

      toast.success(t("fuel_barcode_defaults_imported"));
    } finally {
      setImporting(false);
    }
  };

  const handleEditorSaved = () => {
    setEditing(null);
    loadData();
  };

  const openEditorFor = (id: string) => {
    const barcode = barcodes.find((b) => b.id === id);
    if (barcode) setEditing(barcode);
  };

  const hasActive = barcodes.some((b) => b.isActive);

  const columns = [
    { key: "provider", header: t("fuel_barcode_provider") },
    { key: "type", header: t("fuel_barcode_recognised_by") },
    { key: "value", header: t("fuel_barcode_column_value") },
    { key: "description", header: t("fuel_barcode_description") },
    { key: "active", header: t("fuel_barcode_active") },
  ];

  const rows = barcodes.map((barcode) => ({
    id: barcode.id,
    cells: {
      provider: PROVIDER_BY_TYPE[barcode.type],
      type: t(TYPE_LABEL_KEYS[barcode.type]),
      value: barcode.value,
      description: barcode.description || "—",
      active: (
        <Toggle
          checked={barcode.isActive}
          onChange={() => handleToggleActive(barcode)}
          disabled={pendingToggleIds.has(barcode.id)}
          label={`${t("fuel_barcode_active")}: ${barcode.value}`}
        />
      ),
    },
  }));

  return (
    <SettingsCard
      title={t("fuel_barcodes_card_title")}
      description={t("fuel_barcode_panel_description")}
      actions={
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={handleImportDefaults}
            disabled={loading || importing}
          >
            {t("fuel_barcode_import_defaults")}
          </button>
          <button
            type="button"
            className={PRIMARY_BUTTON}
            onClick={() => setEditing("new")}
          >
            {t("settings_add")}
          </button>
        </div>
      }
    >
      <p className="mb-4 text-sm text-gray-600">
        {t("fuel_barcode_provider_hint")}
      </p>

      {!loading && !hasActive && (
        <div className="mb-4">
          <StatusBanner tone="warn" title={t("fuel_barcode_defaults_notice")} />
        </div>
      )}

      {editing && (
        <FuelBarcodeEditor
          key={editing === "new" ? "new" : editing.id}
          companyId={companyId}
          barcode={editing === "new" ? undefined : editing}
          onSaved={handleEditorSaved}
          onCancel={() => setEditing(null)}
        />
      )}

      {loading ? (
        <p className="text-sm text-gray-500">{t("loading")}</p>
      ) : (
        <CatalogTable
          columns={columns}
          rows={rows}
          renderRowAction={(id) => (
            <ChangeLink onClick={() => openEditorFor(id)} />
          )}
          empty={t("fuel_barcode_no_data")}
        />
      )}
    </SettingsCard>
  );
}
